import { CONSULTANT_SETUP_OPTIONS } from "./consultant-setup-options.ts";
import { generatePassword } from "../../it-approval/utils/generate-temporary-password.ts";
import { useEffect, useRef, useState, type FormEvent } from "react";
import { Button, Modal, Select, TextField, DatePicker, DatePickerFieldTrigger, ensureV2DatepickerPortal, Tabs, TabsList, TabsTrigger, TabsContent, TimePeriodSelect } from "../../../ui/index.ts";
import { Card } from "../../../shared/card/card.tsx";
import { useHrSetupLookups } from "../../hr-setup/hooks/use-hr-setup-lookups.ts";
import { showError, showSuccess } from "../../../../utils/toast.ts";
import { ConsultantQueueDetails } from "./consultant-queue-details.tsx";
import { validateHrSetupFields, INDIAN_HIDDEN_OFFICE_FIELDS, buildHrSetupPayload, buildItSetupPayload, HR_TEXT_FIELDS, saveConsultantSetup } from "./consultant-setup-api.ts";
import { timePeriodToDays, type TimePeriodValue } from "../../../ui/time-period-select/time-period-utils.ts";
import tabStyles from "../../hr-setup/modals/hr-approval-modal.module.css";
import hrStyles from "../../hr-setup/tabs/hr-approve-tab.module.css";
import styles from "../../it-approval/forms/it-approval-form.module.css";
import { RadioGroup } from "../../../ui/radio-group/radio-group.tsx";

type Employee = Record<string, unknown>;
type Props = { open: boolean; employee: Employee; stage: "IT" | "HR"; onClose: () => void; onSuccess: () => void };
type FormProps = { employee: Employee; busy: boolean; onSave: (body: Record<string, unknown>) => Promise<void>; onCancel: () => void };
const CONTRACT_START_MAX_DATE = new Date(2027, 11, 31);
const CONTRACT_END_MAX_DATE = new Date(2029, 11, 31);

export function ConsultantSetupModal({ open, employee, stage, onClose, onSuccess }: Props) {
  const locked = useRef(false);
  const [busy, setBusy] = useState(false);
  const save = async (body: Record<string, unknown>) => {
    if (locked.current) return;
    locked.current = true;
    setBusy(true);
    try {
      const { data } = await saveConsultantSetup(employee.consultantId, stage, body);
      showSuccess(data?.message ?? (stage === "IT" ? "Caelius email created successfully." : body.decision === "REJECTED" ? "Consultant rejected." : "Consultant approved."));
      onSuccess();
      onClose();
    } catch (error) {
      showError(error instanceof Error ? error.message : "Unable to save consultant setup.");
    } finally {
      locked.current = false;
      setBusy(false);
    }
  };
  return <Modal open={open} onClose={() => { if (!locked.current) onClose(); }} title={stage === "IT" ? "Create Caelius Email" : "Consultant Setup"} size="xl">
    {stage === "IT" ? <ConsultantItForm employee={employee} busy={busy} onSave={save} onCancel={onClose} /> : <ConsultantHrForm employee={employee} busy={busy} onSave={save} onCancel={onClose} />}
  </Modal>;
}

function ConsultantItForm({ employee, busy, onSave, onCancel }: FormProps) {
  const [email, setEmail] = useState(String(employee.officialEmail ?? employee.ccEmail ?? ""));
  const dob = typeof employee.dob === "string" ? new Date(employee.dob) : null;
  const password = generatePassword(typeof employee.firstName === "string" ? employee.firstName : "", dob);
  const submit = async (event: FormEvent) => {
    event.preventDefault();
    try { await onSave(buildItSetupPayload(email, password)); }
    catch (error) { showError(error instanceof Error ? error.message : "Check email and password."); }
  };
  return <form className={styles.form} onSubmit={submit}>
    <div className={styles.readOnlyRow}><Card className={styles.formCard}>
      {[
        ["firstName", "First Name"],
        ["lastName", "Last Name"],
        ["preferredName", "Preferred Name"],
        ["personalEmail", "Personal Email Address"],
        ["phoneNumber", "Contact Number"],
      ]
        .filter(([key]) => {
          if (key === "preferredName") {
            return employee.consultantType !== "INDIAN_CONSULTANT";
          }

          return true;
        })
        .map(([key, label]) => (
          <TextField
            key={key}
            id={`consultant-it-${key}`}
            label={label}
            value={String(employee[key] ?? "Not provided")}
            readOnly
            disabled
          />
        ))}      <div className={styles.passwordRow}>
        <TextField id="consultant-temporary-password" label="Password" value={password} readOnly />
        <Button type="button" variant="muted-outline" disabled={busy || !password} onClick={async () => { try { await navigator.clipboard.writeText(password); showSuccess("Password copied."); } catch { showError("Unable to copy password."); } }}>Copy</Button>
      </div>
    </Card></div>
    <div className={styles.actionSection}>
      <div className={styles.inputWrapper}><TextField id="consultant-official-email" label="Caelius Email" type="email" required placeholder="Enter Caelius Email" value={email} disabled={busy} onChange={(event) => setEmail(event.target.value)} /></div>
      <div className={styles.actions}><Button type="button" variant="muted-outline" disabled={busy} onClick={onCancel}>Cancel</Button><Button type="submit" disabled={busy}>{busy ? "Submitting..." : "Submit"}</Button></div>
    </div>
  </form>;
}

function ConsultantHrForm({ employee, busy, onSave, onCancel }: FormProps) {
  const isIndian = employee.consultantType === "INDIAN_CONSULTANT";
  const lookups = useHrSetupLookups();
  const { fetchDesignations } = lookups;
  const [department, setDepartment] = useState("");
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [values, setValues] = useState<Record<string, string>>(() => Object.fromEntries([
    ...HR_TEXT_FIELDS.map(([key]) => [key, String(employee[key] ?? (key === "region" ? employee.consultantType === "INDIAN_CONSULTANT" ? "INDIA" : "US" : key === "employeeSubType" ? employee.consultantType ?? "" : ""))]),
    ...["reportingManagerId", "designationId", "leaveManagerId", "caeliusProjectContactId", "techStack"].map((key) => [key, String(employee[key] ?? "")]),
  ]));
  const tech = employee.tech === true;
  const [activeTab, setActiveTab] = useState("personal");
  const initialNotice = String(employee.noticePeriodDays ?? "30");
  const [noticePreset, setNoticePreset] = useState(["30", "60", "90"].includes(initialNotice) ? initialNotice : "custom");
  const [customNotice, setCustomNotice] = useState<TimePeriodValue>({ amount: Number(initialNotice), unit: "day" });
  useEffect(() => ensureV2DatepickerPortal(), []);
  useEffect(() => { if (lookups.error) showError(lookups.error); }, [lookups.error]);
  useEffect(() => { const controller = new AbortController(); void fetchDesignations(department, controller.signal); return () => controller.abort(); }, [department, fetchDesignations]);
  const update = (key: string, value: string) => {
    setValues((current) => ({ ...current, [key]: value }));
    setErrors((current) => ({ ...current, [key]: "" }));
  };
  const submit = async (event: FormEvent) => {
    event.preventDefault();
    const nextErrors = validateHrSetupFields({ ...values, departmentId: department, noticePeriodDays: values.noticePeriodDays || "30" }, String(employee.consultantType ?? ""));
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length) return;
    try { await onSave(buildHrSetupPayload({ ...values, noticePeriodDays: values.noticePeriodDays || "30" }, tech, "APPROVED", "", String(employee.consultantType ?? ""))); }
    catch (error) { showError(error instanceof Error ? error.message : "Check the HR setup details."); }
  };
  return <Tabs value={activeTab} onValueChange={setActiveTab} className={tabStyles.tabsRoot}>
    <TabsList className={tabStyles.tabsList}>
      <TabsTrigger value="personal" className={tabStyles.tabTrigger} disabled={busy}>Personal</TabsTrigger>
      <TabsTrigger value="office" className={tabStyles.tabTrigger} disabled={busy}>Office Details</TabsTrigger>
    </TabsList>
    <TabsContent value="personal" className={tabStyles.tabPanel}><ConsultantQueueDetails employee={employee} section="personal" /></TabsContent>
    <TabsContent value="office" className={tabStyles.tabPanel}>
      <form className={hrStyles.form} onSubmit={submit} noValidate>
        <div className={hrStyles.grid}>
          <Select
            id="consultant-department"
            label="Department"
            required
            placeholder="Select Department"
            value={department}
            error={errors.departmentId}
            options={lookups.departments}
            disabled={busy || lookups.loading}
            onChange={(value) => {
              setDepartment(value);
              setErrors((current) => ({ ...current, departmentId: "" }));
              update("designationId", "");
            }}
          />          {([
            ["designationId", "Designation", lookups.designations],
            ["reportingManagerId", "Reporting Manager", lookups.managers],
            ["leaveManagerId", "Leave Manager", lookups.managers],
            ["caeliusProjectContactId", "Caelius Project Contact", lookups.projectContacts],
          ] as const).filter(([key]) => !isIndian || !INDIAN_HIDDEN_OFFICE_FIELDS.has(key)).map(([key, label, options]) => (
            <Select
              key={key}
              id={`consultant-hr-${key}`}
              label={label}
              placeholder={`Select ${label}`}
              searchable
              required
              value={values[key]} error={errors[key]}
              options={options}
              disabled={
                busy ||
                lookups.loading ||
                (key === "designationId" && lookups.designationsLoading)
              }
              onChange={(value) => update(key, value)}
            />


          ))}
      


          {HR_TEXT_FIELDS.filter(
            ([key]) =>
              key !== "noticePeriodDays" && 
              key !== "gender" &&
              !(
                isIndian && INDIAN_HIDDEN_OFFICE_FIELDS.has(key)
              )
          ).map(([key, label, type]) => CONSULTANT_SETUP_OPTIONS[key] ? <Select key={key} id={`consultant-hr-${key}`} label={label} placeholder={`Select ${label}`} required options={CONSULTANT_SETUP_OPTIONS[key]} value={values[key]} error={errors[key]} disabled={busy} onChange={(value) => update(key, value)} /> : type === "date" ? <DatePicker key={key} id={`consultant-hr-${key}`} label={label} required={key !== "contractEndDate" && key !== "dob"} error={errors[key]}
            selected={values[key] ? new Date(values[key].slice(0, 10) + "T00:00:00") : null} onChange={(date) => update(key, date ? [date.getFullYear(), String(date.getMonth() + 1).padStart(2, "0"), String(date.getDate()).padStart(2, "0")].join("-") : "")} minDate={key === "contractEndDate" && values.contractStartDate ? new Date(values.contractStartDate.slice(0, 10) + "T00:00:00") : undefined} maxDate={key === "contractStartDate" ? CONTRACT_START_MAX_DATE : key === "contractEndDate" ? CONTRACT_END_MAX_DATE : undefined} disabled={busy} customInput={<DatePickerFieldTrigger />} /> : <TextField required key={key} id={`consultant-hr-${key}`} label={label} placeholder={`Enter ${label}`} type={type ?? "text"} min={type === "number" ? 0 : undefined} step={type === "number" ? key === "noticePeriodDays" ? 1 : "any" : undefined} value={values[key]} error={errors[key]} disabled={busy} onChange={(event) => update(key, event.target.value)} />)}

          <Select id="consultant-notice-period" error={errors.noticePeriodDays} label="Notice Period"  value={noticePreset} options={[{ value: "30", label: "1 Month" }, { value: "60", label: "2 Months" }, { value: "90", label: "3 Months" }, { value: "custom", label: "Custom" }]} disabled={busy} onChange={(value) => { setNoticePreset(value); update("noticePeriodDays", value === "custom" ? String(timePeriodToDays(customNotice.amount, customNotice.unit)) : value); }} />

          {noticePreset === "custom" && <TimePeriodSelect id="consultant-custom-notice-period" error={errors.noticePeriodDays} value={customNotice} onChange={(value) => { setCustomNotice(value); update("noticePeriodDays", String(timePeriodToDays(value.amount, value.unit))); }} disabled={busy} />}
          {!isIndian && <RadioGroup
            label="Gender"
            name="consultant-gender" error={errors.gender}
            value={values.gender}
            onChange={(value) => update("gender", value)}
            required
            options={[
              { label: "Male", value: "MALE" },
              { label: "Female", value: "FEMALE" },
              { label: "Others", value: "OTHERS" },
            ]}
            disabled={busy}
          />}
          <Select required id="consultant-tech-stack" label="Tech Stack" placeholder="Select Tech Stack" value={values.techStack} error={errors.techStack} options={lookups.techStacks} disabled={busy || lookups.loading} onChange={(value) => update("techStack", value)} />
        </div>
        <div className={hrStyles.actions}><Button type="button" variant="muted-outline" disabled={busy} onClick={onCancel}>Cancel</Button><Button type="submit" disabled={busy}>{busy ? "Submitting..." : "Approve"}</Button></div>
      </form>
    </TabsContent>
  </Tabs>;
}
